'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useUser } from '@kit/supabase/hooks/use-user';

import { chromeContent } from '../_content/chrome';
import { authHref, localeFromPath, route } from '../_lib/marketing-routes';
import { Wordmark } from './wordmark';

/**
 * Marketing site header – 1:1 port of chrome.js header (classes/markup identical),
 * locale-aware via usePathname (DE/EN/FR). Sticky nav, active-link via pathname,
 * mobile burger with body-scroll lock. Anti-Profiling bleibt über den Footer
 * erreichbar; die Haupt-Nav führt stattdessen auf /whatsapp.
 */
export function SiteHeader() {
  const pathname = usePathname();
  const locale = localeFromPath(pathname);
  const c = chromeContent[locale];
  const [open, setOpen] = useState(false);

  // Eingeloggt? Dann „Zur Plattform" statt Anmelden/Kostenlos starten.
  // Client-Check (Marketing ist statisch) – kurzer Default auf die Knöpfe für
  // Abgemeldete, bis der Auth-State da ist (die meisten Besucher sind abgemeldet).
  const { data: user } = useUser();

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const isActive = (href: string) => pathname === href;

  const nav = [
    { href: route('service', locale), label: c.nav.service },
    { href: route('warumLocari', locale), label: c.nav.warumLocari },
    { href: route('whatsapp', locale), label: c.nav.whatsapp },
    { href: route('preise', locale), label: c.nav.preise },
    { href: route('faq', locale), label: c.nav.faq },
  ];

  return (
    <>
      <header className="nav" role="banner">
        <div className="nav-inner container">
          <Link
            href={route('home', locale)}
            className="nav-brand"
            aria-label={c.brandAria}
          >
            <Wordmark />
          </Link>

          <nav className="nav-links" aria-label="Hauptnavigation">
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                gap: 'var(--space-1)',
              }}
            >
              {nav.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className={
                      isActive(item.href) ? 'nav-link is-active' : 'nav-link'
                    }
                    aria-current={isActive(item.href) ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="nav-actions">
            {user ? (
              // Auftraggeber 07.10.2026: sichtbarer Knopf in derselben Gestalt
              // wie „Kostenlos starten". Der runde Avatar daneben ist entfallen:
              // er führte nur auf denselben Link (Anweisung 548).
              <Link
                href="/home"
                className="btn btn-marke"
                data-test="site-header-cta-app"
              >
                {c.auth.toApp}
              </Link>
            ) : (
              <>
                <Link href={authHref('signIn', locale)} className="nav-login">
                  {c.auth.signIn}
                </Link>
                {/* Auftraggeber 07.10.2026: gefüllt in Indigo (`btn-marke`) —
                    sichtbar, aber nicht Vermillion; die Vermillion-Fläche
                    gehört dem großen Knopf im Hero bzw. im Schluss-Aufruf. */}
                <Link
                  href={authHref('signUp', locale)}
                  className="btn btn-marke"
                  data-test="site-header-cta-signup"
                >
                  {c.auth.signUp}
                </Link>
              </>
            )}
          </div>

          <button
            className="nav-burger"
            aria-expanded={open}
            aria-controls="navMobile"
            aria-label={c.openMenu}
            onClick={() => setOpen((o) => !o)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="3" y1="7" x2="21" y2="7" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="17" x2="21" y2="17" />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile-Menü AUSSERHALB des <header>: .nav hat backdrop-filter und wäre
          sonst Containing-Block für dieses position:fixed-Overlay (→ Menü würde
          in die Header-Box gequetscht). Bleibt in .marketing-scope, Styles greifen. */}
      <div
        className={open ? 'nav-mobile is-open' : 'nav-mobile'}
        id="navMobile"
        aria-hidden={!open}
      >
        <ul>
          {nav.map((item) => (
            <li key={item.href}>
              <Link
                href={item.href}
                aria-current={isActive(item.href) ? 'page' : undefined}
                onClick={() => setOpen(false)}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
        <div className="nav-mobile-actions">
          {user ? (
            <Link
              href="/home"
              className="btn btn-marke btn-lg"
              data-test="site-header-mobile-cta-app"
              onClick={() => setOpen(false)}
            >
              {c.auth.toApp}
            </Link>
          ) : (
            <>
              <Link
                href={authHref('signUp', locale)}
                className="btn btn-marke btn-lg"
                data-test="site-header-mobile-cta-signup"
                onClick={() => setOpen(false)}
              >
                {c.auth.signUp}
              </Link>
              <Link
                href={authHref('signIn', locale)}
                className="btn btn-secondary btn-lg"
                onClick={() => setOpen(false)}
              >
                {c.auth.signIn}
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}
